import React from 'react';
import { Box, Container, Typography, Button, Grid, Card, CardContent, Link } from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ChatIcon from '@mui/icons-material/Chat';
import PreviewIcon from '@mui/icons-material/Preview';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import TranslateIcon from '@mui/icons-material/Translate';

const FeatureCard = ({ title, description, icon }: { title: string, description: string, icon: React.ReactNode }) => (
  <Card variant="outlined" sx={{ height: '100%', borderColor: 'rgba(0,0,0,0.08)', borderRadius: 2 }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ mb: 2, color: 'primary.main' }}>
        {icon}
      </Box>
      <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {description}
      </Typography>
    </CardContent>
  </Card>
);

const TraeGuide = () => {
  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh', pb: 8 }}>
      {/* Navbar */}
      <Box sx={{ bgcolor: 'black', py: 2, color: 'white' }}>
        <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ bgcolor: '#00dc82', width: 24, height: 24, borderRadius: 4, display: 'inline-block' }} />
              TRAE
            </Typography>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3 }}>
              <Link href="#" color="inherit" underline="none">产品</Link>
              <Link href="#" color="inherit" underline="none">文档</Link>
              <Link href="#" color="inherit" underline="none">常见问题</Link>
              <Link href="#" color="inherit" underline="none">社区</Link>
            </Box>
          </Box>
          <Button variant="outlined" color="inherit" size="small" sx={{ borderRadius: 20 }}>登录</Button>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ bgcolor: 'black', color: 'white', pt: 8, pb: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <Box component="span" sx={{ bgcolor: '#00dc82', width: 32, height: 32, borderRadius: 6 }} />
                <Typography variant="h4" fontWeight="bold">TRAE</Typography>
              </Box>
              <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                字节跳动旗下的AI代码编辑器
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.8, mb: 4, fontWeight: 'normal', lineHeight: 1.5 }}>
                Trae与AI深度集成，提供智能问答、代码自动补全以及基于Agent的AI自动编程能力
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button variant="contained" size="large" sx={{ bgcolor: '#ea4335', '&:hover': { bgcolor: '#d32f2f' }, borderRadius: 1, px: 3 }}>
                  前往中文官网
                </Button>
                <Button variant="outlined" color="inherit" size="large" sx={{ borderRadius: 1, px: 3 }}>
                  国际版官网
                </Button>
                <Button variant="outlined" color="inherit" size="large" sx={{ borderRadius: 1, px: 3 }}>
                  中文文档
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  width: '100%', 
                  height: 300, 
                  bgcolor: '#1e1e1e', 
                  borderRadius: 2, 
                  border: '1px solid #333',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.5)'
                }}
              >
                 {/* Mock Editor UI */}
                 <Box sx={{ p: 2, borderBottom: '1px solid #333', display: 'flex', gap: 1 }}>
                   <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff5f56' }} />
                   <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
                   <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#27c93f' }} />
                 </Box>
                 <Box sx={{ p: 3, color: '#d4d4d4', fontFamily: 'monospace' }}>
                   <Typography>The Real AI Engineer</Typography>
                   <Typography color="success.main" sx={{ mt: 1 }}>&gt; 智能无限，协作无间</Typography>
                 </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Grid */}
      <Container maxWidth="lg" sx={{ mt: -6, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FeatureCard 
              title="安装与设置" 
              description="从官网下载安装后，启动Trae，点击“开始”按钮进入初始配置流程。选择主题（暗色、亮色、深蓝）和显示语言（简体中文、English），然后点击“继续”。可选择从 VS Code 或 Cursor 中导入配置。"
              icon={<CodeIcon fontSize="large" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureCard 
              title="智能代码生成与优化" 
              description="Trae 能够通过自然语言输入生成代码片段，并提供代码优化建议。开发者只需用自然语言描述需求，Trae 就能生成相应的代码，支持代码补全、优化和重构，帮助开发者高效编程。"
              icon={<AutoFixHighIcon fontSize="large" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureCard 
              title="AI驱动的交互模式" 
              description="Trae 提供了 Chat 和 Builder 两种交互模式。Chat 模式支持代码问题解答和代码更新建议；Builder 模式则可以基于用户需求直接生成完整的代码项目，支持通过多轮聊天生成项目级代码。"
              icon={<ChatIcon fontSize="large" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureCard 
              title="便捷的项目预览与调试" 
              description="Trae 提供了 Webview 功能，支持在 IDE 内直接预览 Web 页面，方便前端开发和调试。开发者可以在编写代码的同时，实时预览页面效果，及时发现和解决问题。"
              icon={<PreviewIcon fontSize="large" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureCard 
              title="多模态交互" 
              description="Trae 支持上传图片以澄清需求，结合上下文分析整个代码库，生成更精准的代码。例如，开发者可以上传一个流程图或设计图，Trae 会根据图片内容理解需求，生成相应的代码。"
              icon={<ImageSearchIcon fontSize="large" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureCard 
              title="原生中文支持" 
              description="Trae 从底层设计上支持中文，界面语言全面中文化，适合中文开发者使用。对于中文开发者来说，使用 Trae 可以避免在英文和中文之间频繁切换，提高工作效率和体验。"
              icon={<TranslateIcon fontSize="large" />}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default TraeGuide;
